import { flowRight, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { hot } from 'react-hot-loader';
import noop from 'lodash/noop';
import DocumentTitle from 'react-document-title';
import styled from 'styled-components';
import { slate30, slate70, white } from '@zocdoc-mezzanine/foundations-colors';
import { Button } from '@zocdoc-frontend-common/components-button';
import { FormRow } from '@zocdoc-frontend-common/styles-grid';
import {
    breakpoint,
    HideFor,
    ShowFor,
} from '@zocdoc-frontend-common/styles-grid';
import {
    Column,
    Row,
    columnEnd,
    columnOffset,
    columnSize,
} from '@zocdoc-frontend-common/styles-grid';
import { Radio } from '@zocdoc-frontend-common/components-radio';
import ErrorMessage from '@zocdoc-frontend-common/components-errormessage';
import { TakeoverModal } from '@zocdoc-frontend-common/components-modal';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import {
    withCustomMetrics,
    Metrics,
    MetricsClickable,
    EVENT_INITIATORS,
    INTERACTION_TYPES,
} from '@zocdoc/frontend-metrics';

import LifestanceMentalHealthSection from '../../components/MentalHealthQuestionnaire';
import { InsuranceCardLightBox } from '../../components/InsuranceCardLightBox';
import { InsuranceCardSection } from '../../components/InsuranceCardSection';
import InNetworkOnlyWarning from '../../components/InNetworkOnlyWarning';
import InsurancePayForMyselfModal from '../../components/InsurancePayForMyselfModal';

import UsingInsuranceConfirmation from '../../components/AppointmentRequirements/components/UsingInsuranceConfirmation';
import DoctorCardHeader from '../../components/DoctorCardHeader';

import {
    AddSubpatientButton,
    NewSubpatientForm,
    OutOfNetworkModal,
    SelectPatientRadioGroup,
} from '../../components';

import { PAGE_STRINGS } from './constants';
import { APPOINTMENT_DETAILS_STRINGS } from '../AppointmentDetails/constants';
import { APPOINTMENT_INFO_STRINGS } from '../AppointmentInfo/constants';
import { SELECT_PATIENT_STRINGS } from '../SelectPatient/constants';
import { isVaccineProcedure } from '../../../Booking/utils/isCovidVaccine';

import { SyncOverlay } from '../AppointmentDetails/';
import {
    AppointmentInfoForm,
    AppointmentTimeNotice,
} from '../../components/AppointmentInfoForm';
import RescheduleTimeForm from '../../components/RescheduleTimeForm';
import SecureBookingMessage from '../../components/SecureBookingMessage';
import {
    formatAppointmentTimeForDisplay,
    formatAppointmentDateAndTime,
    formatAppointmentTimeTodayTomorrow,
} from '../../utils/formatAppointmentTimeForDisplay';
import StaticReviewAndBook from '../../components/StaticReviewAndBook';
import { renderPhoneVerificationPage } from '../PhoneVerification';
import AppointmentDetailsForm from '../../components/AppointmentDetailsForm';
import PhoneInputBlock from '../../components/PhoneInputBlock';
import { DoctorNotes } from '../../components/DoctorNotes';
import { completeLUXPageTransition } from '../../utils/LUXUtils';
import { InsuranceFormWrapper } from '../../components/InsuranceFormWrapper';
import NewOrExistingVerification from '../NewOrExistingVerification';
import CoronaErrorView from '../../components/CoronaErrorView';
import { YES_NO_VALUES } from '../../components/YesNoRadioGroup/constants';
import CovidScreenRequirement from '../../components/CovidScreenRequirement';

export class ReviewAndBookPageView extends React.Component {
    static propTypes = {
        appointmentTime: PropTypes.string,
        coronaSymptomsText: PropTypes.string,
        coronaContactsText: PropTypes.string,
        coronaLocationText: PropTypes.string,
        disableButton: PropTypes.bool,
        disableContinue: PropTypes.bool,
        hasUsingInsuranceRequirement: PropTypes.bool,
        inNetworkOnlyBookableText: PropTypes.string,
        shouldShowInNetworkOnlyWarning: PropTypes.bool,
        insuranceCarrierId: PropTypes.string,
        insuranceCarrierName: PropTypes.string,
        insurancePlanId: PropTypes.string,
        insurancePlanName: PropTypes.string,
        insuranceIdRequired: PropTypes.bool,
        isNewPatient: PropTypes.bool,
        isReschedule: PropTypes.bool,
        locationId: PropTypes.string,
        locationTimeZoneId: PropTypes.string.isRequired,
        onBookAppointmentClick: PropTypes.func,
        onChangeCarrier: PropTypes.func,
        onChangePlan: PropTypes.func,
        onClickCarrier: PropTypes.func,
        onClickPlan: PropTypes.func,
        onEditClick: PropTypes.func,
        onPatientChange: PropTypes.func,
        onRadioChange: PropTypes.func,
        onContinueNewExistingVerifiation: PropTypes.func,
        phoneNumberRequired: PropTypes.bool,
        procedureId: PropTypes.string,
        providerId: PropTypes.string,
        sendMetric: PropTypes.func,
        showNewExistingVerification: PropTypes.bool,
        shouldShowStaticReviewAndBook: PropTypes.bool,
        shouldShowCoronaErrorPage: PropTypes.bool,
        showEmergencyNotice: PropTypes.bool,
        showGovernmentInsuranceModal: PropTypes.bool,
        showTimesGrid: PropTypes.bool,
        hasAppointmentTimeError: PropTypes.bool,
        trackPreloadInsuranceCarrier: PropTypes.func,
        trackPreloadInsurancePlan: PropTypes.func,
        updateCoronaRequirementsValues: PropTypes.func,
        updatingIsExistingPatient: PropTypes.bool,
        trackGtmContainerLoad: PropTypes.func,
        carrierId: PropTypes.string,
        planId: PropTypes.string,
        insuranceIsAccepted: PropTypes.bool,
        insuranceIsValidating: PropTypes.bool,
        shouldShowInsuranceAdvocacy: PropTypes.bool,
        showLifestanceSection: PropTypes.bool,
        __forceShowInsuranceAdvocacy: PropTypes.bool,
        bookingId: PropTypes.string,
        surveyQuestionsData: PropTypes.shape({
            questions: PropTypes.arrayOf(
                PropTypes.shape({
                    answers_selected: PropTypes.arrayOf(
                        PropTypes.shape({
                            answer_id: PropTypes.number,
                            display_text: PropTypes.string,
                            free_text_response: PropTypes.string,
                            is_free_text: PropTypes.bool,
                        })
                    ),
                    display_text: PropTypes.string,
                    event_timestamp_utc: PropTypes.string,
                    event_type: PropTypes.string,
                    input_type: PropTypes.string,
                    possible_answers: PropTypes.arrayOf(
                        PropTypes.shape({
                            answer_id: PropTypes.number,
                            display_text: PropTypes.string,
                            free_text_response: PropTypes.string,
                            is_free_text: PropTypes.bool,
                        })
                    ),
                    question_id: PropTypes.number,
                    question_order: PropTypes.number,
                })
            ),
            booking_id: PropTypes.string,
            client_platform: PropTypes.string,
            session_id: PropTypes.string,
            survey_id: PropTypes.string,
            survey_type: PropTypes.string,
            survey_subtype: PropTypes.string,
            tracking_id: PropTypes.string,
            user_agent: PropTypes.string,
        }),
    };

    static defaultProps = {
        onChangeCarrier: noop,
        onChangePlan: noop,
        onClickCarrier: noop,
        onClickPlan: noop,
        trackPreloadInsuranceCarrier: noop,
        trackPreloadInsurancePlan: noop,
        showGovernmentInsuranceModal: false,
        sendMetric: noop,
        __forceShowInsuranceAdvocacy: false,
        bookingId: null,
    };

    constructor(props) {
        super(props);
        this.state = {
            coronaSymptomsRadioState: null,
            coronaContactsRadioState: null,
            coronaLocationsRadioState: null,
            shouldShowCoronaLocationsError: false,
            shouldShowCoronaSymptomsError: false,
            shouldShowCoronaContactsError: false,
            didSelectVerification: false,
        };

        this._appointmentPickerRef = React.createRef();
    }

    componentDidMount() {
        const {
            appointmentDetails: { bookingState },
            location,
            dispatchPageLoadTime,
            trackGtmContainerLoad,
            sendMetric,
        } = this.props;

        // Fire action event if GTM Container loaded on this page
        // GTM component is in node-web-server, ref: https://github.com/Zocdoc/node-web-server/blob/master/src/start/tracking/GoogleTagManager.js#L9
        if (typeof window !== 'undefined' && window.dataLayer) {
            trackGtmContainerLoad();
        }

        if (bookingState) {
            dispatchPageLoadTime('Review and Book');
            completeLUXPageTransition('Review and Book', location);
        }

        if (this.shouldShowCoronaScreen()) {
            sendMetric({
                element: 'CoronavirusScreenerQuestions',
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
            });
        }
    }

    renderReviewAndBook = () => {
        const {
            isNewPatient,
            professionalName,
            onContinueNewExistingVerifiation,
            shouldShowStaticReviewAndBook,
            showNewExistingVerification,
        } = this.props;

        const { didSelectVerification } = this.state;

        if (showNewExistingVerification) {
            return (
                // We only show a prefilled radio if the user goes BACK to this step in the browser
                // Therefore this must be null on first view
                <NewOrExistingVerification
                    isNewPatient={didSelectVerification ? isNewPatient : null}
                    professionalName={professionalName}
                    onRadioChange={this.handleNewExistingVerificationChange}
                    onClickContinue={onContinueNewExistingVerifiation}
                />
            );
        }

        return shouldShowStaticReviewAndBook
            ? this.renderStatic()
            : this.renderEditable();
    };

    render() {
        const {
            shouldShowLoadingAnimation,
            bookingId,
            procedureId,
            directoryId,
            surveyQuestionsData,
            isReschedule,
        } = this.props;

        const pageStrings = PAGE_STRINGS;
        const isVaccineApt = isVaccineProcedure(procedureId);
        if (this.props.shouldShowCoronaErrorPage) {
            return <CoronaErrorView procedureId={procedureId} />;
        }

        if (shouldShowLoadingAnimation) {
            return (
                <SyncOverlayContainer>
                    <SyncOverlay
                        scrollIntoView
                        surveyQuestionsData={surveyQuestionsData}
                        isVaccineApt={isVaccineApt}
                        bookingId={bookingId}
                        directoryId={directoryId}
                        isReschedule={isReschedule}
                    />
                </SyncOverlayContainer>
            );
        }

        return (
            <DocumentTitle title={pageStrings.PageTitle}>
                <Wrapper>
                    {this.renderTakeoverModal()}
                    <InsuranceCardLightBox />
                    {this.renderDoctorCard()}
                    <DoctorHeaderApptInfoRow>
                        <AppointmentInfoInputs>
                            {this.maybeRenderAppointmentTimeError()}
                            {this.renderHeader()}
                            {this.renderReviewAndBook()}
                        </AppointmentInfoInputs>
                        {this.renderSecureBookingMessage()}
                    </DoctorHeaderApptInfoRow>
                    {this.maybeRenderOutOfNetworkModal()}
                    {this.maybeRenderPayForMyselfModal()}
                </Wrapper>
            </DocumentTitle>
        );
    }

    renderDoctorCard() {
        const {
            appointmentTime,
            locationId,
            locationTimeZoneId,
            procedureId,
            providerId,
        } = this.props;

        const formattedAppointmentTime = formatAppointmentTimeTodayTomorrow(
            appointmentTime,
            locationTimeZoneId
        );

        return (
            <DoctorCardWrapper>
                <DoctorCardHeader
                    formattedAppointmentTime={formattedAppointmentTime}
                    locationId={locationId}
                    providerId={providerId}
                    procedureId={procedureId}
                    showOldBookingPage={true}
                />
            </DoctorCardWrapper>
        );
    }

    renderStatic() {
        const {
            appointmentDetails,
            appointmentTime,
            hasAppointmentTimeError,
            hasUsingInsuranceRequirement,
            iceRequestToken,
            insuranceCarrierName,
            insuranceCarrierId,
            insurancePlanName,
            insurancePlanId,
            insuranceIdRequired,
            isNewPatient,
            locationTimeZoneId,
            mainPatientName,
            onBookAppointmentClick,
            phoneNumber,
            phoneNumberRequired,
            procedureName,
            showEmergencyNotice,
        } = this.props;

        const formattedAppointmentTime = formatAppointmentTimeForDisplay(
            appointmentTime,
            locationTimeZoneId
        );

        const shouldDisableMemberIdField =
            iceRequestToken && this.insuranceAvailableButNotShared();

        const expandedPatientSection = (
            <React.Fragment>
                {this.renderSelectPatientRadioGroup()}
                {this.renderPhoneInputBlock()}
            </React.Fragment>
        );

        const expandedInsuranceSection = (
            <React.Fragment>
                {this.renderChooseYourInsurance()}
                <InsuranceCardSection />
                <AppointmentDetailsForm
                    {...this.props.appointmentDetails}
                    insuranceIdRequired={insuranceIdRequired}
                    shouldDisableMemberIdField={shouldDisableMemberIdField}
                />
            </React.Fragment>
        );

        const expandedAppointmentSection = (
            <React.Fragment>
                {this.renderAppointmentFormInformation()}
                <DoctorNotes />
            </React.Fragment>
        );

        return (
            <StaticReviewAndBookWrapper>
                <StaticReviewAndBook
                    expandedPatientSection={expandedPatientSection}
                    expandedInsuranceSection={expandedInsuranceSection}
                    expandedAppointmentSection={expandedAppointmentSection}
                    appointmentDetails={appointmentDetails}
                    formattedAppointmentTime={formattedAppointmentTime}
                    hasAppointmentTimeError={hasAppointmentTimeError}
                    hasUsingInsuranceRequirement={hasUsingInsuranceRequirement}
                    insuranceCarrierName={insuranceCarrierName}
                    insuranceCarrierId={insuranceCarrierId}
                    insurancePlanName={insurancePlanName}
                    insurancePlanId={insurancePlanId}
                    isNewPatient={isNewPatient}
                    mainPatientName={mainPatientName}
                    onBookAppointmentClick={onBookAppointmentClick}
                    phoneNumber={phoneNumber}
                    phoneNumberRequired={phoneNumberRequired}
                    procedureName={procedureName}
                    shouldDisableMemberIdField={shouldDisableMemberIdField}
                    shouldShowEmergencyNotice={showEmergencyNotice}
                />
            </StaticReviewAndBookWrapper>
        );
    }

    renderEditable() {
        return (
            <div data-test="booking-web-app">
                {this.renderSelectPatientRadioGroup()}
                {this.renderPhoneInputBlock()}
                {this.renderChooseYourInsurance()}
                {this.renderControls()}
                {this.maybeRenderCoronaVirusScreen()}
                {this.maybeRenderLifeStanceMentalHealth()}
                <BookAppointmentButtonWrapper>
                    {this.renderBookAppointmentButton()}
                </BookAppointmentButtonWrapper>
            </div>
        );
    }

    maybeRenderOutOfNetworkModal = () => {
        const {
            showGovernmentInsuranceModal,
            showNewExistingVerification,
            shouldShowMentalHealthOONBenefits,
            professional,
            sendMetric,
        } = this.props;

        // Prevents both modals from appearing at once. GovernmentInsuranceModal takes priority
        // Also prevents modal from showing during new/existing verification step
        if (
            showGovernmentInsuranceModal ||
            showNewExistingVerification ||
            isEmpty(professional)
        ) {
            return null;
        }
        return (
            <OutOfNetworkModal
                shouldShowMentalHealthOONBenefits={
                    shouldShowMentalHealthOONBenefits
                }
                sendMetric={sendMetric}
            />
        );
    };

    maybeRenderPayForMyselfModal = () => {
        const {
            showGovernmentInsuranceModal,
            showNewExistingVerification,
            professional,
            professionalName,
            inNetworkOnlyBookableText,
            insuranceCarrierId,
            insurancePlanId,
            procedureId,
        } = this.props;

        if (
            showGovernmentInsuranceModal ||
            showNewExistingVerification ||
            isEmpty(professional) ||
            !inNetworkOnlyBookableText
        ) {
            return null;
        }

        return (
            <InsurancePayForMyselfModal
                data-test="insurance-pay-for-myself-modal"
                professional={professional}
                procedureId={procedureId}
                professionalName={professionalName}
                insuranceCarrierId={insuranceCarrierId}
                insurancePlanId={insurancePlanId}
            />
        );
    };

    handleNewExistingVerificationChange = value => {
        this.setState({ didSelectVerification: true });
        this.props.onRadioChange(value);
    };

    onAppointmentTimeErrorClick = () => {
        if (
            this._appointmentPickerRef &&
            this._appointmentPickerRef.current &&
            this._appointmentPickerRef.current.offsetTop
        ) {
            window.scrollTo({
                top: this._appointmentPickerRef.current.offsetTop,
                left: 0,
                behavior: 'smooth',
            });
        }
    };

    maybeRenderAppointmentTimeError = () => {
        const {
            hasAppointmentTimeError,
            showNewExistingVerification,
            shouldShowStaticReviewAndBook,
        } = this.props;

        if (
            shouldShowStaticReviewAndBook &&
            !showNewExistingVerification &&
            hasAppointmentTimeError
        ) {
            return (
                <MetricsClickable
                    component="ErrorMessageBox"
                    element="AppointmentTimeNoLongerAvailable"
                    category="Booking - Appt info"
                    action="Clicked Appointment No Longer Available Error"
                >
                    <HeaderAppointmentTimeNotice
                        onClick={this.onAppointmentTimeErrorClick}
                        showNotice
                    >
                        The appointment time you selected is no longer
                        available. Please select a new time below.
                    </HeaderAppointmentTimeNotice>
                </MetricsClickable>
            );
        }
    };

    renderControls() {
        const {
            appointmentDetails,
            iceRequestToken,
            insuranceIdRequired,
        } = this.props;

        const shouldDisableMemberIdField =
            iceRequestToken && this.insuranceAvailableButNotShared();

        return (
            <div data-test="controls-section">
                <InsuranceCardSection />
                <AppointmentDetailsForm
                    {...appointmentDetails}
                    insuranceIdRequired={insuranceIdRequired}
                    shouldDisableMemberIdField={shouldDisableMemberIdField}
                    shouldRenderDoctorNotes={false}
                />
                {this.renderAppointmentFormInformation()}
                <DoctorNotes />
            </div>
        );
    }

    renderAppointmentFormInformation() {
        const {
            appointmentTime,
            directoryId,
            insuranceCarrierId,
            insurancePlanId,
            isNewPatient,
            isReschedule,
            locationId,
            locationTimeZoneId,
            onEditClick,
            onRadioChange,
            onTimeSlotClicked,
            procedureId,
            procedureMayNotBeCovered,
            professionalName,
            providerId,
            showEmergencyNotice,
            showTimesGrid,
            hasAppointmentTimeError,
            updatingIsExistingPatient,
        } = this.props;

        const formattedAppointmentTime = formatAppointmentTimeForDisplay(
            appointmentTime,
            locationTimeZoneId
        );

        return (
            <FormRow>
                <Metrics section="AppointmentInfoForm">
                    <AppointmentInfoForm
                        appointmentPickerRef={this._appointmentPickerRef}
                        directoryId={directoryId}
                        formattedAppointmentTime={formattedAppointmentTime}
                        insuranceCarrierId={insuranceCarrierId}
                        insurancePlanId={insurancePlanId}
                        isNewPatient={isNewPatient}
                        isReschedule={isReschedule}
                        locationId={locationId}
                        onEditClick={onEditClick}
                        onRadioChange={onRadioChange}
                        onTimeSlotClicked={onTimeSlotClicked}
                        procedureId={procedureId}
                        procedureMayNotBeCovered={procedureMayNotBeCovered}
                        professionalName={professionalName}
                        providerId={providerId}
                        showEmergencyNotice={showEmergencyNotice}
                        showTimesGrid={showTimesGrid}
                        hasAppointmentTimeError={hasAppointmentTimeError}
                        updatingIsExistingPatient={updatingIsExistingPatient}
                    />
                </Metrics>
            </FormRow>
        );
    }

    maybeRenderCoronaVirusScreen() {
        if (!this.shouldShowCoronaScreen()) {
            return null;
        }
        const {
            coronaSymptomsText,
            coronaContactsText,
            coronaLocationText,
        } = this.props;
        const {
            shouldShowCoronaSymptomsError,
            shouldShowCoronaContactsError,
            shouldShowCoronaLocationsError,
        } = this.state;

        return (
            <React.Fragment>
                {coronaLocationText && (
                    <CovidScreenRequirement
                        dataTest="corona-screen-question-location"
                        text={coronaLocationText}
                        onRadioChange={this.handleChangeCoronaLocationsRadio}
                        hasError={shouldShowCoronaLocationsError}
                    />
                )}
                {coronaSymptomsText && (
                    <CovidScreenRequirement
                        dataTest="corona-screen-question-symptoms"
                        text={coronaSymptomsText}
                        onRadioChange={this.handleChangeCoronaSymptomsRadio}
                        hasError={shouldShowCoronaSymptomsError}
                    />
                )}
                {coronaContactsText && (
                    <CovidScreenRequirement
                        dataTest="corona-screen-question-contacts"
                        text={coronaContactsText}
                        onRadioChange={this.handleChangeCoronaContactsRadio}
                        hasError={shouldShowCoronaContactsError}
                    />
                )}
            </React.Fragment>
        );
    }

    handleChangeCoronaSymptomsRadio = val => {
        this.sendCoronaRadioButtonSelectMetric('CoronaSymptomsRadio', val);

        this.setState({
            coronaSymptomsRadioState: val === YES_NO_VALUES.Yes,
            shouldShowCoronaSymptomsError: false,
        });
    };

    handleChangeCoronaContactsRadio = val => {
        this.sendCoronaRadioButtonSelectMetric('CoronaContactsRadio', val);

        this.setState({
            coronaContactsRadioState: val === YES_NO_VALUES.Yes,
            shouldShowCoronaContactsError: false,
        });
    };

    handleChangeCoronaLocationsRadio = val => {
        this.sendCoronaRadioButtonSelectMetric('CoronaLocationsRadio', val);

        this.setState({
            coronaLocationsRadioState: val === YES_NO_VALUES.Yes,
            shouldShowCoronaLocationsError: false,
        });
    };

    sendCoronaRadioButtonSelectMetric = (element, value) => {
        const { sendMetric } = this.props;
        sendMetric({
            element,
            eventDetails: {
                value,
            },
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.SELECT,
        });
    };

    renderBookAppointmentButton() {
        const {
            disableButton,
            disableContinue,
            showInsuranceError,
            showMissingFieldsError,
            showVaccineDisclaimer,
        } = this.props;

        const isDisabled = Boolean(
            this.iceCardIsRequiredButMissing() ||
                disableContinue ||
                disableButton
        );

        const missingFieldsError =
            showInsuranceError || showMissingFieldsError ? (
                <ErrorMessage data-test="missing-fields-error-shown">
                    Please complete all required fields
                </ErrorMessage>
            ) : (
                <span data-test="missing-fields-error-hidden" />
            );

        return (
            <div>
                <UsingInsuranceConfirmation />
                {showVaccineDisclaimer && (
                    <VaccineDisclaimer>
                        By booking this appointment, you verify that you have
                        accurately filled out the COVID-19 Vaccine Prescreening
                        Questionnaire and acknowledge that Zocdoc may share
                        information about this booking with public health
                        entities as permitted or required by law. All bookings
                        for COVID-19 vaccination are subject to vaccine
                        availability.
                    </VaccineDisclaimer>
                )}
                {this.maybeRenderInNetworkOnlyWarning()}
                <Button
                    data-test="book-button"
                    data-uem-id="book-button"
                    disabled={isDisabled}
                    fluid
                    id="BookApptButton"
                    onClick={this.handleBookAppointmentClick}
                >
                    {APPOINTMENT_DETAILS_STRINGS.BookAppointment}
                </Button>
                {missingFieldsError}
            </div>
        );
    }

    maybeRenderInsuranceError() {
        const { showInsuranceError } = this.props;

        if (showInsuranceError) {
            return (
                <ErrorMessage data-test="insurance-error-message">
                    Please select a valid insurance carrier and plan.
                </ErrorMessage>
            );
        }
    }

    maybeRenderInNetworkOnlyWarning() {
        const {
            inNetworkOnlyBookableText,
            procedureId,
            specialtyId,
            insuranceCarrierId,
            insurancePlanId,
            sendMetric,
            shouldShowInNetworkOnlyWarning,
        } = this.props;

        if (shouldShowInNetworkOnlyWarning && inNetworkOnlyBookableText) {
            return (
                <InNetworkOnlyWarning
                    data-test="in-network-only-warning"
                    inNetworkOnlyBookableText={inNetworkOnlyBookableText}
                    procedureId={procedureId}
                    specialtyId={specialtyId}
                    insuranceCarrierId={insuranceCarrierId}
                    insurancePlanId={insurancePlanId}
                    sendMetric={sendMetric}
                />
            );
        }
    }

    renderTakeoverModal() {
        const {
            appointmentTime,
            emailErrorMessage,
            insuranceCarrierId,
            insurancePlanId,
            isNewPatient,
            locationId,
            locationTimeZoneId,
            nameErrorMessage,
            onClickCancelModal,
            onPhoneVerified,
            onSubpatientCreated,
            onTimeSlotClicked,
            procedureId,
            professionalName,
            providerId,
            showDateError,
            showNewSubpatientForm,
            showPhoneVerificationForm,
            showRescheduleTimeSelectionForm,
            onRescheduleClick,
            trackViewedRescheduleModal,
            trackNewSubpatientClickContinue,
        } = this.props;

        if (showNewSubpatientForm) {
            return (
                <TakeoverModal onClickCancel={onClickCancelModal} showCloseIcon>
                    <NewSubpatientForm
                        emailErrorMessage={emailErrorMessage}
                        nameErrorMessage={nameErrorMessage}
                        onSubpatientCreated={onSubpatientCreated}
                        showDateError={showDateError}
                        trackNewSubpatientClickContinue={
                            trackNewSubpatientClickContinue
                        }
                    />
                </TakeoverModal>
            );
        } else if (showPhoneVerificationForm) {
            return (
                <TakeoverModal onClickCancel={onClickCancelModal} showCloseIcon>
                    {renderPhoneVerificationPage(onPhoneVerified)}
                </TakeoverModal>
            );
        } else if (showRescheduleTimeSelectionForm) {
            return (
                <TakeoverModal onClickCancel={onClickCancelModal} showCloseIcon>
                    <RescheduleTimeForm
                        appointmentTime={formatAppointmentDateAndTime(
                            appointmentTime,
                            locationTimeZoneId
                        )}
                        insuranceCarrierId={insuranceCarrierId}
                        insurancePlanId={insurancePlanId}
                        isNewPatient={isNewPatient}
                        locationId={locationId}
                        onClickContinue={onRescheduleClick}
                        onTimeSlotClicked={onTimeSlotClicked}
                        procedureId={procedureId}
                        professionalName={professionalName}
                        providerId={providerId}
                        trackViewedRescheduleModal={trackViewedRescheduleModal}
                    />
                </TakeoverModal>
            );
        }
    }

    renderHeader() {
        const { showNewExistingVerification } = this.props;
        if (showNewExistingVerification) {
            return null;
        }

        const pageStrings = PAGE_STRINGS;

        return (
            <div>
                <HideFor xsmall xxsmall>
                    <HeaderTitle data-test="title-show-for-small">
                        {pageStrings.Header}
                    </HeaderTitle>
                </HideFor>

                <ShowFor xsmall xxsmall>
                    <HeaderTitle>{pageStrings.Header}</HeaderTitle>
                </ShowFor>
            </div>
        );
    }

    renderSelectPatientRadioGroup() {
        const {
            mainPatientId,
            mainPatientName,
            onNewPatientClick,
            onPatientChange,
            selectedPatient,
            subpatients,
        } = this.props;

        const subpatientRadios = subpatients.map(subpatient => (
            <Radio
                key={subpatient.patientId.toString()}
                className="exclude-from-playback"
                data-test={`sub-patient-${subpatient.patientId}`}
                text={(subpatient.firstName || '')
                    .concat(' ')
                    .concat(subpatient.lastName)}
                value={subpatient.patientId.toString()}
            />
        ));
        return (
            <FormRow>
                <FormLabel>
                    {SELECT_PATIENT_STRINGS.WhoWillBeSeeingTheDoctor}
                </FormLabel>
                <SelectPatientRadioGroup
                    name="patient"
                    onChangeCallback={onPatientChange}
                    orientation="vertical"
                    selectedValue={selectedPatient}
                >
                    <Radio
                        className="exclude-from-playback"
                        data-test="main-patient"
                        text={mainPatientName}
                        value={mainPatientId.toString()}
                    />
                    {subpatientRadios}
                    <AddSubpatientButton
                        data-test="someone-else"
                        handleNewPatientClick={onNewPatientClick}
                        text={SELECT_PATIENT_STRINGS.SomeoneElse}
                        value="someoneElse"
                    />
                </SelectPatientRadioGroup>
            </FormRow>
        );
    }

    renderPhoneInputBlock() {
        const {
            phoneInputBlockHasError,
            phoneNumber,
            phoneNumberRequired,
            onAddOrEditPhoneNumberClick,
            trackClickAddPhone,
            trackClickEditPhone,
        } = this.props;

        return (
            <PhoneInputBlock
                hasError={phoneInputBlockHasError}
                onAddOrEditPhoneNumberClick={onAddOrEditPhoneNumberClick}
                phoneNumber={phoneNumber}
                phoneNumberRequired={phoneNumberRequired || !phoneNumber}
                trackClickAddPhone={trackClickAddPhone}
                trackClickEditPhone={trackClickEditPhone}
                data-test="phone-input-block"
            />
        );
    }

    renderChooseYourInsurance() {
        const {
            appointmentTime,
            insuranceCarrierId,
            insurancePlanId,
            insuranceIsAccepted,
            insuranceIsValidating,
            onChangeCarrier,
            onChangePlan,
            onClickCarrier,
            onClickPlan,
            procedureId,
            professional,
            shouldShowMentalHealthOONBenefits,
            specialtyId,
            trackPreloadInsuranceCarrier,
            trackPreloadInsurancePlan,
            shouldShowInsuranceAdvocacy,
            sendMetric,
            __forceShowInsuranceAdvocacy,
        } = this.props;

        // appointmentTime only exists when the booking state is received
        const receivedBookingState = !!appointmentTime;

        return (
            <FormRow>
                <FormLabel>{APPOINTMENT_INFO_STRINGS.Insurance}</FormLabel>
                <div className="skiptranslate">
                    <InsuranceFormWrapper
                        insuranceCarrierId={insuranceCarrierId}
                        insurancePlanId={insurancePlanId}
                        onChangeCarrier={onChangeCarrier}
                        onClickCarrier={onClickCarrier}
                        onClickPlan={onClickPlan}
                        onChangePlan={onChangePlan}
                        procedureId={procedureId}
                        providerFullName={professional.approvedFullName}
                        receivedBookingState={receivedBookingState}
                        specialtyId={specialtyId}
                        trackPreloadInsuranceCarrier={
                            trackPreloadInsuranceCarrier
                        }
                        trackPreloadInsurancePlan={trackPreloadInsurancePlan}
                        shouldShowMentalHealthOONBenefits={
                            shouldShowMentalHealthOONBenefits
                        }
                        shouldShowInsuranceAdvocacy={
                            shouldShowInsuranceAdvocacy
                        }
                        insuranceIsValidating={insuranceIsValidating}
                        sendMetric={sendMetric}
                        insuranceIsAccepted={
                            insuranceIsAccepted && !insuranceIsValidating
                        }
                        __forceShowInsuranceAdvocacy={
                            __forceShowInsuranceAdvocacy
                        }
                    />
                </div>

                {this.maybeRenderInsuranceError()}
            </FormRow>
        );
    }

    renderSecureBookingMessage() {
        if (!this.props.showNewExistingVerification) {
            return (
                <SecureBookingMessageWrapper>
                    <SecureBookingMessage color={slate70} />
                </SecureBookingMessageWrapper>
            );
        }
    }

    insuranceAvailableButNotShared = () => {
        const {
            availableInsuranceCardDataId,
            isInsuranceCardShared,
        } = this.props;

        return availableInsuranceCardDataId && !isInsuranceCardShared;
    };

    iceCardIsRequiredButMissing = () => {
        const { iceRequestToken, insuranceIdRequired } = this.props;

        // Show error when box is unchecked but is required to be checked by provider
        return (
            this.insuranceAvailableButNotShared() &&
            iceRequestToken &&
            insuranceIdRequired
        );
    };

    handleBookAppointmentClick = (...args) => {
        if (!this.shouldShowCoronaScreen()) {
            this.props.onBookAppointmentClick(...args);
        } else {
            const {
                coronaSymptomsText,
                coronaContactsText,
                coronaLocationText,
            } = this.props;
            const {
                coronaSymptomsRadioState,
                coronaContactsRadioState,
                coronaLocationsRadioState,
            } = this.state;
            let canContinue = true;
            if (coronaSymptomsText && coronaSymptomsRadioState === null) {
                canContinue = false;
                this.setState({ shouldShowCoronaSymptomsError: true });
            }
            if (coronaContactsText && coronaContactsRadioState === null) {
                canContinue = false;
                this.setState({ shouldShowCoronaContactsError: true });
            }
            if (coronaLocationText && coronaLocationsRadioState === null) {
                canContinue = false;
                this.setState({ shouldShowCoronaLocationsError: true });
            }
            if (canContinue) {
                this.props.updateCoronaRequirementsValues({
                    symptoms: coronaSymptomsRadioState,
                    contacts: coronaContactsRadioState,
                    location: coronaLocationsRadioState,
                });
                this.props.onBookAppointmentClick(...args);
            }
        }
    };

    shouldShowCoronaScreen = () => {
        const {
            coronaSymptomsText,
            coronaContactsText,
            coronaLocationText,
        } = this.props;
        return coronaSymptomsText || coronaContactsText || coronaLocationText;
    };

    maybeRenderLifeStanceMentalHealth = () => {
        const { showLifeStanceSection } = this.props;

        if (showLifeStanceSection) {
            return (
                <FormRow data-test="lifestance-mental-health-section">
                    <LifestanceMentalHealthSection />
                </FormRow>
            );
        }
        return null;
    };
}

const Wrapper = styled.div`
    margin-top: 0px;
`;

const HeaderTitle = styled.div`
    font: 20px/24px ${FontFamily.SharpSansBold};
    margin-bottom: 24px;
    text-align: center;

    ${breakpoint.small} {
        font-size: 24px;
        margin-bottom: 32px;
        text-align: left;
    }
`;

const HeaderAppointmentTimeNotice = styled(AppointmentTimeNotice)`
    margin-bottom: 18px;
`;

const SyncOverlayContainer = styled.div``;

const DoctorHeaderApptInfoRow = styled(Row)`
    max-width: 504px;
`;

const AppointmentInfoInputs = styled(Column)`
    ${breakpoint('xsmall', { down: true })} {
        ${columnSize(22)};
        ${columnOffset(1)};
        ${columnEnd};
    }
`;

const FormLabel = styled.div`
    font: 15px/24px ${FontFamily.SharpSansSemibold};
    padding-bottom: 4px;
`;

const BookAppointmentButtonWrapper = styled.div`
    margin: 24px 0 40px;
`;

const SecureBookingMessageWrapper = styled.div`
    margin-bottom: 100px;
    text-align: center;
    color: ${slate70};
    font: 13px/18px ${FontFamily.SharpSansSemibold};
`;

const StaticReviewAndBookWrapper = styled.div`
    margin-bottom: 60px;

    ${breakpoint.small} {
        margin-bottom: 28px;
    }
`;

const DoctorCardWrapper = styled.div`
    margin-bottom: 20px;
    float: none;
    background-color: ${white};
    border-top: solid 1px ${slate30};
    border-bottom: solid 1px ${slate30};
    padding-top: 10px;

    ${breakpoint.small} {
        border: solid 1px ${slate30};
    }
`;

const VaccineDisclaimer = styled(FormRow)`
    font: 13px/20px ${FontFamily.SharpSansMedium};
`;

export default flowRight(
    hot(module),
    withCustomMetrics()
)(ReviewAndBookPageView);
